import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  NotificationLog,
  NotificationLogFilters,
  NotificationLogPage,
  NotificationStatus,
  SortDirection,
  SortField,
} from "../types";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";

// Constants
const DEFAULT_PAGE_SIZE = 20;

export function useNotificationLogs() {
  // Pagination and filter state - similar to AssignmentManagement
  const [filters, setFilters] = useState<NotificationLogFilters>({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sortBy: "scheduledTime", // Changed to match backend default
    sortDirection: "desc",
    statuses: [],
    notificationTypes: [],
    recipientTypes: [],
  });

  // Individual filter states for UI
  const [search, setSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Update filters when individual filter states change
  useEffect(() => {
    const formatDateForBackend = (dateString: string) => {
      if (!dateString) return undefined;
      // Convert YYYY-MM-DD to OffsetDateTime format (start of day)
      return `${dateString}T00:00:00Z`;
    };

    setFilters((prev) => ({
      ...prev,
      statuses: selectedStatuses,
      notificationTypes: selectedChannels, // Maps channels to notificationTypes for backend
      recipientSearch: search,
      scheduledFromDate: formatDateForBackend(dateFrom),
      scheduledToDate: formatDateForBackend(dateTo),
      page: 0, // Reset to first page when filters change
    }));
  }, [selectedStatuses, selectedChannels, search, dateFrom, dateTo]);

  const { data: notificationLogsData, isLoading } = useQuery({
    queryKey: ["notificationLogs", filters],
    queryFn: async (): Promise<NotificationLogPage> => {
      try {
        // Build query parameters
        const params = new URLSearchParams();
        params.append("page", filters.page.toString());
        params.append("size", filters.size.toString());
        if (filters.sortBy) params.append("sortBy", filters.sortBy);
        if (filters.sortDirection)
          params.append("sortDirection", filters.sortDirection);

        // Add array parameters
        filters.statuses?.forEach((status) =>
          params.append("statuses", status),
        );
        filters.notificationTypes?.forEach((type) =>
          params.append("notificationTypes", type),
        );
        filters.recipientTypes?.forEach((type) =>
          params.append("recipientTypes", type),
        );

        // Add search parameters
        if (filters.recipientSearch)
          params.append("recipientSearch", filters.recipientSearch);
        if (filters.templateNameSearch)
          params.append("templateNameSearch", filters.templateNameSearch);
        if (filters.scheduledFromDate)
          params.append("scheduledFromDate", filters.scheduledFromDate);
        if (filters.scheduledToDate)
          params.append("scheduledToDate", filters.scheduledToDate);
        if (filters.subjectSearch)
          params.append("subjectSearch", filters.subjectSearch);

        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BASE_API_URL}notification?${params.toString()}`,
        );
        return response.data;
      } catch (error) {
        console.error("Failed to fetch notification logs:", error);
        toast({
          title: "Error",
          description: "Failed to fetch notification logs. Please try again.",
          variant: "destructive",
        });
        // Return empty page structure
        return {
          content: [],
          page: {
            size: DEFAULT_PAGE_SIZE,
            number: 0,
            totalElements: 0,
            totalPages: 0,
          },
        };
      }
    },
  });

  const handleSort = (field: SortField) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortDirection:
        prev.sortBy === field && prev.sortDirection === "asc" ? "desc" : "asc",
      page: 0, // Reset to first page when sorting
    }));
  };

  const handleResend = async (notificationLogId: string) => {
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_BASE_API_URL}notification/${notificationLogId}`,
      );

      toast({
        title: "Success",
        description: "Notification has been resent.",
        variant: "success",
      });

      // Invalidate the query to refresh the data
      queryClient.invalidateQueries({ queryKey: ["notificationLogs"] });
    } catch (error) {
      console.error("Failed to resend notification:", error);
      toast({
        title: "Error",
        description: "Failed to resend notification. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = async (notificationLogId: string) => {
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_BASE_API_URL}notification/${notificationLogId}/cancel`,
      );

      toast({
        title: "Success",
        description: "Notification has been cancelled.",
        variant: "success",
      });

      // Invalidate the query to refresh the data
      queryClient.invalidateQueries({ queryKey: ["notificationLogs"] });
    } catch (error) {
      console.error("Failed to cancel notification:", error);
      toast({
        title: "Error",
        description: "Failed to cancel notification. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // Get unique values for filter options from current data
  const uniqueTemplates = notificationLogsData?.content
    ? [...new Set(notificationLogsData.content.map((log) => log.templateName))]
    : [];

  const uniqueChannels = notificationLogsData?.content
    ? [...new Set(notificationLogsData.content.map((log) => log.channel))]
    : ["EMAIL", "SMS"];

  const getStatusStats = () => {
    if (!notificationLogsData) return null;

    const logs = notificationLogsData.content;
    const pending = logs.filter((log) => log.status === "PENDING").length;
    const sent = logs.filter((log) => log.status === "SENT").length;
    const failed = logs.filter((log) => log.status === "FAILED").length;
    const cancelled = logs.filter((log) => log.status === "CANCELLED").length;

    return { pending, sent, failed, cancelled };
  };

  return {
    notificationLogs: notificationLogsData?.content || [],
    isLoading,
    stats: getStatusStats(),

    // Pagination
    totalPages: notificationLogsData?.page?.totalPages || 0,
    currentPage: notificationLogsData?.page?.number ?? (filters.page || 0),
    totalElements: notificationLogsData?.page?.totalElements || 0,
    pageSize: filters.size,
    handlePageChange,

    // Sorting
    sortField: (filters.sortBy || "scheduledTime") as SortField,
    sortDirection: (filters.sortDirection || "desc") as SortDirection,
    handleSort,

    // Filtering
    search,
    setSearch,
    selectedStatuses,
    setSelectedStatuses,
    selectedChannels,
    setSelectedChannels,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,

    // Actions
    handleResend,
    handleCancel,

    // Filter options
    uniqueTemplates,
    statuses: Object.values(NotificationStatus),
    channels: uniqueChannels,
  };
}
